import { useTranslation } from 'react-i18next';
import {
  X,
  ArrowRightLeft,
  Clock,
  CheckCircle,
  Truck,
  PackageCheck,
  XCircle,
  AlertTriangle,
  ArrowRight,
} from 'lucide-react';
import type { Transfer } from '../../../api/queries/transfer.queries';

interface TransferDetailModalProps {
  transfer: Transfer;
  onClose: () => void;
  onApprove?: () => void;
  onDispatch?: () => void;
  onReceive?: () => void;
}

export default function TransferDetailModal({
  transfer,
  onClose,
  onApprove,
  onDispatch,
  onReceive,
}: TransferDetailModalProps) {
  const { t, i18n } = useTranslation();

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString(i18n.language === 'th' ? 'th-TH' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateShort = (date: string) => {
    return new Date(date).toLocaleDateString(i18n.language === 'th' ? 'th-TH' : 'en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'request':
      case 'pending_hq_approval':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'approved':
      case 'picking':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'pending_dispatch_verification':
      case 'in_transit':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'pending_receiving':
      case 'partially_received':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'received':
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'rejected':
      case 'cancelled':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'warehouse_to_branch':
        return t('transfers.warehouseToBranch');
      case 'branch_to_branch':
        return t('transfers.branchToBranch');
      case 'branch_to_warehouse':
        return t('transfers.branchToWarehouse');
      default:
        return type;
    }
  };

  const getConditionColor = (condition: string) => {
    switch (condition) {
      case 'good':
        return 'text-green-600 bg-green-50';
      case 'damaged':
      case 'expired':
      case 'wrong_item':
      case 'quality_issue':
        return 'text-red-600 bg-red-50';
      case 'quantity_short':
        return 'text-orange-600 bg-orange-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getSLAColor = (status?: string) => {
    switch (status) {
      case 'on_track':
        return 'text-green-600 bg-green-50';
      case 'at_risk':
        return 'text-orange-600 bg-orange-50';
      case 'breached':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const fromLocation = typeof transfer.fromLocationId === 'object' ? transfer.fromLocationId : null;
  const toLocation = typeof transfer.toLocationId === 'object' ? transfer.toLocationId : null;
  const requestedBy = typeof transfer.requestedBy === 'object' ? transfer.requestedBy : null;
  const approvedBy = typeof transfer.approvedBy === 'object' ? transfer.approvedBy : null;
  const dispatchedBy = typeof transfer.dispatchedBy === 'object' ? transfer.dispatchedBy : null;
  const receivedBy = typeof transfer.receivedBy === 'object' ? transfer.receivedBy : null;

  const canApprove = ['request', 'pending_hq_approval'].includes(transfer.status) && onApprove;
  const canDispatch = ['approved', 'picking', 'pending_dispatch_verification'].includes(transfer.status) && onDispatch;
  const canReceive = ['in_transit', 'pending_receiving', 'partially_received'].includes(transfer.status) && onReceive;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-lg bg-card flex flex-col">
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ArrowRightLeft className="h-5 w-5 text-primary" />
            <div>
              <h2 className="text-xl font-bold">{transfer.transferNumber}</h2>
              <p className="text-sm text-muted-foreground">{getTypeLabel(transfer.type)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded border text-xs ${getStatusColor(transfer.status)}`}>
              {t(`transfers.${transfer.status}`)}
            </span>
            {transfer.isReplacementTransfer && (
              <span className="px-2 py-1 rounded bg-purple-50 text-purple-700 text-xs">
                {t('transfers.replacement')}
              </span>
            )}
            {transfer.slaTracking?.slaStatus && (
              <span className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${getSLAColor(transfer.slaTracking.slaStatus)}`}>
                <Clock className="h-3 w-3" />
                {t(`transfers.sla_${transfer.slaTracking.slaStatus}`)}
              </span>
            )}
            <button onClick={onClose} className="p-2 hover:bg-secondary rounded ml-2">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Rejection Summary */}
            {transfer.hasRejectedItems && transfer.rejectionSummary && (
              <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <p className="text-sm font-medium text-red-800">{t('transfers.rejectionSummary')}</p>
                </div>
                <div className="text-sm text-red-700">
                  <p>{t('transfers.totalRejected')}: {transfer.rejectionSummary.totalRejected}</p>
                  {transfer.rejectionSummary.rejectionReasons.length > 0 && (
                    <p>{t('transfers.reasons')}: {transfer.rejectionSummary.rejectionReasons.join(', ')}</p>
                  )}
                </div>
              </div>
            )}

            {/* Location Flow */}
            <div className="flex items-center justify-center gap-4 p-4 rounded-lg bg-secondary/50">
              <div className="text-center">
                <p className="text-xs text-muted-foreground">{t('transfers.from')}</p>
                <p className="font-medium">{fromLocation?.name || '-'}</p>
                <p className="text-xs text-muted-foreground">{fromLocation?.code}</p>
              </div>
              <ArrowRight className="h-6 w-6 text-primary" />
              <div className="text-center">
                <p className="text-xs text-muted-foreground">{t('transfers.to')}</p>
                <p className="font-medium">{toLocation?.name || '-'}</p>
                <p className="text-xs text-muted-foreground">{toLocation?.code}</p>
              </div>
            </div>

            {/* Status Timeline */}
            <div className="flex items-center gap-3 overflow-x-auto pb-2">
              <TimelineStep
                icon={<Clock className="h-4 w-4" />}
                label={t('transfers.requested')}
                date={formatDateShort(transfer.requestedDate)}
                user={requestedBy?.username}
                isComplete={true}
              />
              {transfer.approvedDate && (
                <>
                  <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <TimelineStep
                    icon={<CheckCircle className="h-4 w-4" />}
                    label={t('transfers.approved')}
                    date={formatDateShort(transfer.approvedDate)}
                    user={approvedBy?.username}
                    isComplete={true}
                  />
                </>
              )}
              {transfer.dispatchDate && (
                <>
                  <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <TimelineStep
                    icon={<Truck className="h-4 w-4" />}
                    label={t('transfers.dispatched')}
                    date={formatDateShort(transfer.dispatchDate)}
                    user={dispatchedBy?.username}
                    isComplete={true}
                  />
                </>
              )}
              {transfer.receivedDate && (
                <>
                  <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <TimelineStep
                    icon={<PackageCheck className="h-4 w-4" />}
                    label={t('transfers.received')}
                    date={formatDateShort(transfer.receivedDate)}
                    user={receivedBy?.username}
                    isComplete={true}
                  />
                </>
              )}
              {transfer.status === 'rejected' && (
                <>
                  <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <TimelineStep
                    icon={<XCircle className="h-4 w-4" />}
                    label={t('transfers.rejected')}
                    isComplete={true}
                    isError={true}
                  />
                </>
              )}
            </div>

            {/* Key Info Grid */}
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">{t('transfers.requestedDate')}</p>
                <p className="font-medium">{formatDate(transfer.requestedDate)}</p>
              </div>
              {transfer.expectedDeliveryDate && (
                <div>
                  <p className="text-muted-foreground">{t('transfers.expectedDelivery')}</p>
                  <p className="font-medium">{formatDate(transfer.expectedDeliveryDate)}</p>
                </div>
              )}
              {transfer.actualDeliveryDate && (
                <div>
                  <p className="text-muted-foreground">{t('transfers.actualDelivery')}</p>
                  <p className="font-medium">{formatDate(transfer.actualDeliveryDate)}</p>
                </div>
              )}
            </div>

            {/* SLA Info */}
            {transfer.slaTracking && (
              <div className="p-3 rounded-lg border">
                <h3 className="text-sm font-medium mb-2">{t('transfers.slaTracking')}</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">{t('transfers.slaStatus')}</p>
                    <p className={`font-medium ${getSLAColor(transfer.slaTracking.slaStatus).replace('bg-', 'text-').split(' ')[0]}`}>
                      {t(`transfers.sla_${transfer.slaTracking.slaStatus}`)}
                    </p>
                  </div>
                  {transfer.slaTracking.hoursRemaining !== undefined && transfer.slaTracking.hoursRemaining > 0 && (
                    <div>
                      <p className="text-muted-foreground">{t('transfers.hoursRemaining')}</p>
                      <p className="font-medium">{transfer.slaTracking.hoursRemaining} {t('common.hours')}</p>
                    </div>
                  )}
                  {transfer.slaTracking.hoursOverdue !== undefined && transfer.slaTracking.hoursOverdue > 0 && (
                    <div>
                      <p className="text-muted-foreground">{t('transfers.hoursOverdue')}</p>
                      <p className="font-medium text-red-600">{transfer.slaTracking.hoursOverdue} {t('common.hours')}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Items Table */}
            <div>
              <h3 className="text-sm font-medium mb-3">{t('transfers.items')}</h3>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50">
                    <tr>
                      <th className="text-left py-2 px-3">{t('transfers.ingredient')}</th>
                      <th className="text-right py-2 px-3">{t('transfers.requested')}</th>
                      <th className="text-right py-2 px-3">{t('transfers.approved')}</th>
                      <th className="text-right py-2 px-3">{t('transfers.dispatched')}</th>
                      <th className="text-right py-2 px-3">{t('transfers.received')}</th>
                      <th className="text-left py-2 px-3">{t('transfers.condition')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transfer.items.map((item) => {
                      const ingredient = typeof item.ingredientId === 'object' ? item.ingredientId : null;
                      const hasDiscrepancy = item.receivedQuantity !== undefined &&
                        item.receivedQuantity !== (item.dispatchedQuantity || item.approvedQuantity || item.requestedQuantity);

                      return (
                        <tr key={item._id} className={`border-t ${hasDiscrepancy ? 'bg-yellow-50' : ''}`}>
                          <td className="py-2 px-3">
                            <div>
                              <span>{ingredient?.name?.en || '-'}</span>
                              {item.batchNumber && (
                                <p className="text-xs text-muted-foreground">
                                  {t('transfers.batch')}: {item.batchNumber}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="py-2 px-3 text-right">{item.requestedQuantity} {item.unit}</td>
                          <td className="py-2 px-3 text-right">
                            {item.approvedQuantity !== undefined ? `${item.approvedQuantity} ${item.unit}` : '-'}
                          </td>
                          <td className="py-2 px-3 text-right">
                            {item.dispatchedQuantity !== undefined ? `${item.dispatchedQuantity} ${item.unit}` : '-'}
                          </td>
                          <td className="py-2 px-3 text-right">
                            {item.receivedQuantity !== undefined ? (
                              <span className={hasDiscrepancy ? 'text-orange-600 font-medium' : ''}>
                                {item.receivedQuantity} {item.unit}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="py-2 px-3">
                            {item.receivingCondition && (
                              <span className={`px-2 py-0.5 rounded text-xs ${getConditionColor(item.receivingCondition)}`}>
                                {t(`transfers.${item.receivingCondition}`)}
                              </span>
                            )}
                            {item.isRejected && (
                              <span className="ml-1 px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">
                                {t('transfers.rejected')}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Rejection Reason */}
            {transfer.rejectionReason && (
              <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                <p className="text-sm font-medium text-red-800">{t('transfers.rejectionReason')}:</p>
                <p className="text-sm text-red-700 mt-1">{transfer.rejectionReason}</p>
              </div>
            )}

            {/* Notes */}
            {transfer.notes && (
              <div>
                <h3 className="text-sm font-medium mb-2">{t('common.notes')}</h3>
                <p className="text-sm text-muted-foreground">{transfer.notes}</p>
              </div>
            )}

            {/* Internal Notes */}
            {transfer.internalNotes && (
              <div>
                <h3 className="text-sm font-medium mb-2">{t('transfers.internalNotes')}</h3>
                <p className="text-sm text-muted-foreground">{transfer.internalNotes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors"
          >
            {t('common.close')}
          </button>
          {canApprove && (
            <button
              onClick={onApprove}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2"
            >
              <CheckCircle className="h-4 w-4" />
              {t('transfers.approveTransfer')}
            </button>
          )}
          {canDispatch && (
            <button
              onClick={onDispatch}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2"
            >
              <Truck className="h-4 w-4" />
              {t('transfers.dispatchTransfer')}
            </button>
          )}
          {canReceive && (
            <button
              onClick={onReceive}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2"
            >
              <PackageCheck className="h-4 w-4" />
              {t('transfers.receiveTransfer')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Timeline Step Component
function TimelineStep({
  icon,
  label,
  date,
  user,
  isComplete,
  isError,
}: {
  icon: React.ReactNode;
  label: string;
  date?: string;
  user?: string;
  isComplete: boolean;
  isError?: boolean;
}) {
  return (
    <div className={`flex items-center gap-2 p-2 rounded-lg flex-shrink-0 ${
      isError
        ? 'bg-red-100'
        : isComplete
        ? 'bg-green-100'
        : 'bg-gray-100'
    }`}>
      <div className={isError ? 'text-red-600' : isComplete ? 'text-green-600' : 'text-gray-400'}>
        {icon}
      </div>
      <div>
        <p className="text-xs font-medium">{label}</p>
        {date && <p className="text-xs text-muted-foreground">{date}</p>}
        {user && <p className="text-xs text-muted-foreground">{user}</p>}
      </div>
    </div>
  );
}
